import React, {
  useId,
  useState,
  useCallback,
  useEffect,
  useContext
} from "react"
import type { ReactNode, ForwardedRef } from "react"
import classNames from "classnames"

import Button from "../button"
import type { ActionProps, ActionItem, ActionContextType } from "./types"

export type { ActionProps }

export const ActionContext = React.createContext<ActionContextType>({
  actions: [],
  register: () => {},
  remove: () => {}
})

type ActionProviderProps = {
  children: ReactNode
}

export const ActionProvider = ({ children }: ActionProviderProps) => {
  const [actions, setActions] = useState<ActionItem[]>([])

  const remove = useCallback((id: string) => {
    setActions(actions => actions.filter(action => action.id !== id))
  }, [])

  const register = useCallback((id: string, props: ActionProps, ref: ForwardedRef<HTMLButtonElement>) => {
    setActions(actions => {
      let index = actions.findIndex(action => action.id === id)

      if (index == -1) index = actions.length

      return Object.assign([], actions, {
        [index]: {
          ...actions[index],
          ...props,
          ref,
          id
        }
      })
    })
  }, [])

  return (
    <ActionContext.Provider value={{ actions, register, remove }}>
      {children}
    </ActionContext.Provider>
  )
}

const useAction = (id: string, props: ActionProps, ref: ForwardedRef<HTMLButtonElement>) => {
  const { register, remove } = useContext(ActionContext)

  useEffect(() => {
    if (!id) return
    register(id, props, ref)
    return () => remove(id)
  }, [id, props, ref, register, remove])
}

const ActionButton = ({ is, onClick, disabled, children, className, href, icon, style, type, target, rel, telemetry, "data-testid": dataTestId }: ActionItem) => (
  <Button
    is={is}
    onClick={onClick}
    disabled={disabled}
    className={classNames("mx-2 flex-auto justify-center font-semibold", className)}
    href={href}
    icon={icon}
    style={style}
    type={type}
    target={target}
    rel={rel}
    telemetry={telemetry}
    data-testid={dataTestId}
  >
    {children}
  </Button>
)

export const Actions = () => {
  const { actions } = useContext(ActionContext)

  if (actions.length === 0) return null

  const leftActions = actions.filter(a => a.position === "left")
  const rightActions = actions.filter(a => a.position !== "left")
  const hasLeftActions = leftActions.length > 0

  return (
    <div
      className={classNames(
        "flex rounded-b-lg border-t border-gray-200 py-6 px-6 sticky bottom-0 bg-white",
        hasLeftActions ? "justify-between" : "justify-end"
      )}
      data-testid="actions"
    >
      {hasLeftActions && (
        <div className="-ml-2">
          {leftActions.map(action => (
            <ActionButton key={action.id} {...action} />
          ))}
        </div>
      )}
      <div className="-mr-2">
        {rightActions.map(action => (
          <ActionButton key={action.id} {...action} />
        ))}
      </div>
    </div>
  )
}

const Action = React.forwardRef<HTMLButtonElement, ActionProps>((props, ref) => {
  const id = useId()
  useAction(id, props, ref)

  return null
})

export default Action
